import { Check, ChevronDown, Copy, RefreshCw, ServerCrash } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { LegacyGenerationJob } from "~/entities/chat";
import type { ChatGenerationError } from "~/shared/store/useChatErrorsStore";
import { Button } from "~/shared/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/shared/ui/collapsible";
import { cn } from "~/shared/lib/utils";

interface MessageErrorProps {
    messageId: string;
    job?: LegacyGenerationJob | null;
    localError?: ChatGenerationError | null;
    onRetry: () => void;
    isRetrying?: boolean;
}

// Красная карточка ошибки под user-сообщением — рендерится либо из
// локальной ошибки запуска (request 4xx/5xx до создания job), либо
// из failed/expired/canceled job на бэке. Кнопка Retry повторяет
// тот же startGeneration с теми же messageId / settings.
export function MessageError({ messageId, job, localError, onRetry, isRetrying }: MessageErrorProps) {
    const { t } = useTranslation("chat");
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const isJobFailed =
        !!job && (job.status === "failed" || job.status === "expired" || job.status === "canceled");

    if (!localError && !isJobFailed) return null;

    const message = localError?.message ?? job?.errorMessage ?? t("messages.error.fallback");
    const statusCode = localError?.statusCode;
    const code = localError?.code;
    const provider = job?.providerName ?? null;
    const jobId = job?.id ?? null;

    const detailsLines: string[] = [
        `Title: ${t("messages.error.title")}`,
        `Message: ${message}`,
        statusCode != null ? `HTTP: ${statusCode}` : null,
        code ? `Code: ${code}` : null,
        provider ? `Provider: ${provider}` : null,
        jobId ? `Job: ${jobId}` : null,
        `MessageId: ${messageId}`,
    ].filter((line): line is string => line !== null);
    const detailsText = detailsLines.join("\n");

    const handleCopyDetails = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(detailsText);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch (err) {
            console.error("copy failed", err);
        }
    };

    return (
        <div className="mt-3 rounded-lg border border-red-500/10 bg-red-500/[0.06] p-3">
            <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-500/10">
                    <ServerCrash className="h-4 w-4 text-red-400" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-red-400">{t("messages.error.title")}</div>
                    <div className="mt-1 text-xs leading-relaxed text-zinc-400">{message}</div>
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onRetry}
                    disabled={isRetrying}
                    className={cn(
                        "h-7 shrink-0 px-2 text-xs text-red-400 hover:bg-red-500/10 hover:text-red-300",
                        "disabled:opacity-50",
                    )}
                >
                    <RefreshCw className={cn("mr-1 h-3 w-3", isRetrying && "animate-spin")} />
                    {t("messages.error.retry")}
                </Button>
            </div>

            {(statusCode != null || code != null || provider != null || jobId != null) && (
                <Collapsible open={open} onOpenChange={setOpen}>
                    <CollapsibleTrigger className="mt-2 flex items-center gap-1 text-[11px] text-zinc-500 transition-colors hover:text-zinc-300">
                        <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
                        {t("messages.error.technicalDetails")}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <div className="relative mt-2 rounded bg-black/20 p-2 pr-8 font-mono text-[11px] text-zinc-500">
                            {statusCode != null && <div>HTTP: {statusCode}</div>}
                            {code && <div>Code: {code}</div>}
                            {provider && <div>Provider: {provider}</div>}
                            {jobId && <div>Job: {jobId}</div>}
                            <div>Message: {messageId}</div>
                            <button
                                type="button"
                                onClick={handleCopyDetails}
                                aria-label={
                                    copied ? t("messages.actions.copied") : t("messages.error.copyDetails")
                                }
                                title={
                                    copied ? t("messages.actions.copied") : t("messages.error.copyDetails")
                                }
                                className={cn(
                                    "absolute top-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-md",
                                    "text-zinc-500 transition-colors hover:bg-white/5 hover:text-zinc-200",
                                    copied && "text-emerald-400 hover:text-emerald-400",
                                )}
                            >
                                {copied ? (
                                    <Check className="h-3.5 w-3.5" />
                                ) : (
                                    <Copy className="h-3.5 w-3.5" />
                                )}
                            </button>
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            )}
        </div>
    );
}
